import { useCallback, useEffect, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import {
	buildChangeHistoryDataQueryVariables,
	CHANGE_HISTORY_PAGE_SIZE,
} from '../changeHistoryQueryVariables.ts';

import type {
	useChangeHistoryDataQuery$data,
	useChangeHistoryDataQuery$variables,
} from './__generated__/useChangeHistoryDataQuery.graphql.ts';

type UseChangeHistoryDataOptions = {
	contentId?: string;
};

type UseChangeHistoryDataResult = {
	data: useChangeHistoryDataQuery$data | undefined;
	loading: boolean;
	error: Error | null;
	loadNextPage: () => Promise<void>;
	isLoadingNextPage: boolean;
	loadNextPageError: Error | null;
};

const toError = (error: unknown) =>
	error instanceof Error ? error : new Error('Failed to load more change history');

export const useChangeHistoryData = ({
	contentId,
}: UseChangeHistoryDataOptions): UseChangeHistoryDataResult => {
	const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
	const [loadNextPageError, setLoadNextPageError] = useState<Error | null>(null);
	const isLoadingNextPageRef = useRef(false);
	const { data, loading, error, fetchMore } = useQueryInterop<
		useChangeHistoryDataQuery$data,
		useChangeHistoryDataQuery$variables
	>(
		graphql`
			query useChangeHistoryDataQuery($contentId: ID!, $after: String, $first: Long) {
				contentHistory(contentId: $contentId, after: $after, first: $first) {
					nodes {
						by {
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
							displayName
						}
						friendlyWhen
						when
						message
						number
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
				singleContent(id: $contentId, status: ["current", "draft", "archived"]) {
					type
					subType
					space {
						key
					}
					version {
						by {
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
							displayName
						}
						friendlyWhen
						when
						message
						number
					}
				}
			}
		`,
		{
			variables: buildChangeHistoryDataQueryVariables(contentId ?? ''),
			skip: !contentId,
			fetchPolicy: 'cache-first',
		},
	);

	useEffect(() => {
		setLoadNextPageError(null);
		isLoadingNextPageRef.current = false;
		setIsLoadingNextPage(false);
	}, [contentId]);

	const pageInfo = data?.contentHistory?.pageInfo;

	const loadNextPage = useCallback(async () => {
		if (
			!contentId ||
			!pageInfo?.hasNextPage ||
			!pageInfo.endCursor ||
			isLoadingNextPageRef.current
		) {
			return;
		}

		isLoadingNextPageRef.current = true;
		setIsLoadingNextPage(true);
		setLoadNextPageError(null);

		try {
			await fetchMore({
				variables: {
					contentId: contentId ?? '',
					after: pageInfo.endCursor,
					first: CHANGE_HISTORY_PAGE_SIZE,
				},
				updateQuery: (previousData, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contentHistory) {
						return previousData;
					}

					if (!previousData.contentHistory) {
						return fetchMoreResult;
					}

					return {
						...previousData,
						contentHistory: {
							...fetchMoreResult.contentHistory,
							nodes: [
								...(previousData.contentHistory.nodes ?? []),
								...(fetchMoreResult.contentHistory.nodes ?? []),
							],
						},
						singleContent: previousData.singleContent ?? fetchMoreResult.singleContent,
					};
				},
			});
		} catch (fetchMoreError) {
			setLoadNextPageError(toError(fetchMoreError));
		} finally {
			isLoadingNextPageRef.current = false;
			setIsLoadingNextPage(false);
		}
	}, [contentId, fetchMore, pageInfo?.endCursor, pageInfo?.hasNextPage]);

	return {
		data,
		loading,
		error: error ?? null,
		loadNextPage,
		isLoadingNextPage,
		loadNextPageError,
	};
};
